import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ReactLenis, useLenis } from 'lenis/react';
import Icon from './Icon';
import { GradientMark } from './StudentUi';
import { StudentProvider, useStudent } from './StudentContext';
import { STUDENT_BASE, shareTargets } from './studentData';
import './StudentLayout.css';

const MENU_LENIS_OPTIONS = {
  orientation: 'horizontal',
  gestureOrientation: 'vertical',
  smoothWheel: false,
  syncTouch: false,
  autoRaf: true,
  lerp: 0.08,
  overscroll: false,
};

const tabs = [
  {
    id: 'home',
    to: STUDENT_BASE,
    icon: 'home',
    label: 'Home',
    isOn: (path) => path === STUDENT_BASE || path.endsWith('/activity'),
  },
  { id: 'learn', to: `${STUDENT_BASE}/course`, icon: 'learn', label: 'Learn' },
  { id: 'jobs', to: `${STUDENT_BASE}/job`, icon: 'job', label: 'Jobs' },
  { id: 'clubs', to: `${STUDENT_BASE}/club`, icon: 'users', label: 'Clubs' },
  {
    id: 'profile',
    to: `${STUDENT_BASE}/profile`,
    icon: 'user',
    label: 'Profile',
    isOn: (path) => path.includes('/profile') || path.includes('/achievement'),
  },
];

const menuItems = [
  { id: 'learn', label: 'Learn', to: `${STUDENT_BASE}/course`, icon: 'learn', from: '#2563eb', tint: '#4f46e5', isOn: (path) => path.includes('/course') },
  { id: 'jobs', label: 'Jobs', to: `${STUDENT_BASE}/job`, icon: 'job', from: '#8b5cf6', tint: '#6d28d9', isOn: (path) => path.includes('/job') },
  { id: 'clubs', label: 'Clubs', to: `${STUDENT_BASE}/club`, icon: 'users', from: '#10b981', tint: '#059669', isOn: (path) => path.includes('/club') },
  { id: 'achievements', label: 'Achievements', to: `${STUDENT_BASE}/achievements`, icon: 'trophy', from: '#fbbf24', tint: '#f97316', isOn: (path) => path.includes('/achievement') },
  { id: 'profile', label: 'Profile', to: `${STUDENT_BASE}/profile`, icon: 'user', from: '#f472b6', tint: '#fb923c', isOn: (path) => path.includes('/profile') },
];

const MenuMotion = ({ open, onClose, onExited, children }) => {
  const openRef = useRef(open);
  openRef.current = open;
  const lenis = useLenis();

  useLenis((instance) => {
    const shown = instance.limit > 0 ? 1 - instance.scroll / instance.limit : 0;
    const root = instance.rootElement;
    if (root) {
      root.style.backgroundColor = `rgba(10, 10, 40, ${(0.55 * shown).toFixed(3)})`;
    }
    root?.querySelectorAll('.sa-menu-item').forEach((item, index) => {
      const local = Math.min(1, Math.max(0, (shown - index * 0.07) / 0.62));
      item.style.opacity = String(local);
      item.style.transform = `translate3d(${((1 - local) * -18).toFixed(1)}px, 0, 0)`;
    });
  });

  useEffect(() => {
    if (!lenis) return undefined;
    let cancelled = false;
    let frame = 0;
    const place = () => {
      if (cancelled) return;
      if (!lenis.limit) {
        frame = requestAnimationFrame(place);
        return;
      }
      if (openRef.current) {
        lenis.scrollTo(lenis.limit, { immediate: true, force: true });
        lenis.rootElement.dataset.ready = 'true';
        frame = requestAnimationFrame(() => {
          if (!cancelled && openRef.current) {
            lenis.scrollTo(0, { duration: 0.72, force: true });
          }
        });
        return;
      }
      lenis.rootElement.dataset.ready = 'true';
      lenis.scrollTo(lenis.limit, {
        duration: 0.58,
        force: true,
        onComplete: () => {
          if (!openRef.current) onExited();
        },
      });
    };
    frame = requestAnimationFrame(place);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [open, lenis, onExited]);

  return (
    <div className="sa-menu-track">
      <div className="sa-drawer" role="dialog" aria-modal="true" aria-label="Menu" onClick={(event) => event.stopPropagation()}>
        {children}
      </div>
      <button type="button" className="sa-menu-spacer" aria-label="Close menu" tabIndex={-1} onClick={onClose} />
    </div>
  );
};

const StudentSkeleton = () => (
  <div className="sa-skeleton" role="status" aria-live="polite">
    <span className="sa-sr">Loading</span>
    <div className="sa-sk-top">
      <i className="sa-sk sa-sk-logo" />
      <i className="sa-sk sa-sk-title" />
      <i className="sa-sk sa-sk-round" />
    </div>
    <i className="sa-sk sa-sk-line lg" />
    <i className="sa-sk sa-sk-line" />
    <div className="sa-sk-tiles">
      <i className="sa-sk sa-sk-tile" />
      <i className="sa-sk sa-sk-tile" />
      <i className="sa-sk sa-sk-tile" />
    </div>
    <i className="sa-sk sa-sk-card" />
    <i className="sa-sk sa-sk-card" />
    <i className="sa-sk sa-sk-card short" />
  </div>
);

const MenuDrawer = ({ open, onClose, children }) => {
  const [mounted, setMounted] = useState(open);
  const finish = useCallback(() => setMounted(false), []);

  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  if (!mounted) return null;

  return (
    <ReactLenis className="sa-drawer-backdrop" options={MENU_LENIS_OPTIONS}>
      <MenuMotion open={open} onClose={onClose} onExited={finish}>
        {children}
      </MenuMotion>
    </ReactLenis>
  );
};

const StudentShell = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const mainRef = useRef(null);
  const pageRef = useRef(null);
  const [pageReady, setPageReady] = useState(false);
  const { theme, toast, sheetOpen, closeSheet, showToast, menuOpen, closeMenu } = useStudent();

  useEffect(() => {
    const root = pageRef.current;
    if (mainRef.current) mainRef.current.scrollTop = 0;
    closeMenu();
    setPageReady(false);

    let iconsDone = false;
    let finished = false;
    let delayTimer = 0;
    const started = performance.now();
    const finish = () => {
      if (finished || !iconsDone) return;
      const wait = 700 - (performance.now() - started);
      if (wait > 0) {
        if (!delayTimer) delayTimer = window.setTimeout(finish, wait);
        return;
      }
      finished = true;
      setPageReady(true);
    };
    const iconsReady = () => {
      const icons = [...(root?.querySelectorAll('lord-icon') || [])];
      return icons.length > 0 && icons.every((icon) => icon.matches(':state(ready)') || icon.matches(':state(error)'));
    };
    const markIcons = () => {
      if (!iconsReady()) return;
      iconsDone = true;
      finish();
    };

    const maxTimer = window.setTimeout(() => {
      iconsDone = true;
      finish();
    }, 1800);
    const emptyTimer = window.setTimeout(() => {
      if (!root?.querySelector('lord-icon')) {
        iconsDone = true;
        finish();
      }
    }, 120);

    root?.addEventListener('ready', markIcons, true);
    root?.addEventListener('error', markIcons, true);
    const observer = new MutationObserver(markIcons);
    if (root) observer.observe(root, { childList: true, subtree: true });
    markIcons();

    return () => {
      finished = true;
      window.clearTimeout(delayTimer);
      window.clearTimeout(maxTimer);
      window.clearTimeout(emptyTimer);
      root?.removeEventListener('ready', markIcons, true);
      root?.removeEventListener('error', markIcons, true);
      observer.disconnect();
    };
  }, [pathname, closeMenu]);

  const share = (label) => {
    closeSheet();
    showToast(`Shared to ${label}`);
  };

  return (
    <div className="student-shell">
      <div className={`student-app${theme === 'dark' ? ' is-dark' : ''}`}>
        <main className="student-main" ref={mainRef} aria-busy={pageReady ? undefined : true}>
          <div ref={pageRef} className={pageReady ? 'sa-page' : 'sa-page is-pending'} aria-hidden={pageReady ? undefined : true}>
            <Outlet />
          </div>
          {pageReady ? null : <StudentSkeleton />}
        </main>
        <nav className="sa-nav" aria-label="Student">
          {tabs.map((tab) => {
            const on = tab.isOn ? tab.isOn(pathname) : pathname === tab.to;
            return (
              <button key={tab.id} type="button" className={on ? 'on' : ''} onClick={() => navigate(tab.to)}>
                <Icon name={tab.icon} />
                {tab.label}
              </button>
            );
          })}
        </nav>
        <MenuDrawer open={menuOpen} onClose={closeMenu}>
          <div className="sa-top">
            <h1>Menu</h1>
            <button type="button" className="sa-ib" aria-label="Close menu" onClick={closeMenu}>
              <Icon name="close" />
            </button>
          </div>
          <div className="sa-menu-list">
            {menuItems.map((item) => {
              const on = item.isOn(pathname);
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`sa-menu-item${on ? ' on' : ''}`}
                  onClick={() => navigate(item.to)}
                >
                  <GradientMark name={item.icon} from={item.from} to={item.tint} />
                  <span className="sa-sp">{item.label}</span>
                  <Icon name="next" />
                </button>
              );
            })}
          </div>
        </MenuDrawer>
        {sheetOpen ? (
          <div className="sa-sheet" onClick={(event) => { if (event.target === event.currentTarget) closeSheet(); }}>
            <div>
              <div className="sa-b" style={{ fontSize: 17, marginBottom: 4 }}>Share achievement</div>
              <div className="sa-mut">Won 2nd Prize – Inter-College Hackathon</div>
              <div className="sa-share">
                {shareTargets.map((target) => (
                  <button key={target.label} type="button" onClick={() => share(target.label)}>
                    <GradientMark name={target.icon} from={target.color} to={target.color} />
                    {target.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : null}
        {toast ? <div className="sa-toast">{toast}</div> : null}
      </div>
    </div>
  );
};

const StudentLayout = () => (
  <StudentProvider>
    <StudentShell />
  </StudentProvider>
);

export default StudentLayout;
